import { Box, Grid, Stack } from "@mui/material";
import { FC } from "react";
import { LayoutProps } from "../types";
import ImageBlock from "../blocks/image-block";
import HeaderBlock from "../blocks/header-block";
import ContentBlock from "../blocks/content-block";
import StatsBlock from "../blocks/stats-block";
import ActionsBlock from "../blocks/actions-block";
import MetaBlock from "../blocks/meta-block";

const HorizontalLayout: FC<LayoutProps> = ({
  blocks,
  imagePosition,
  spacing,
  theme,
  containerProps,
  showBorder = true,
  showBoxShadow = true,
  disableHover = false,
}) => {
  const getSpacingValue = () => {
    switch (spacing) {
      case "compact":
        return 3;
      case "spacious":
        return 6;
      default:
        return 5;
    }
  };

  const isImageLeft = imagePosition === "left";
  const hasImage = !!blocks.image;

  const getContainerStyles = () => {
    const baseStyles = {
      position: "relative" as const,
      width: "100%",
      padding: getSpacingValue(),
    };

    const borderStyles = showBorder
      ? {
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
        }
      : {};

    const shadowStyles = showBoxShadow
      ? {
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }
      : {};

    const hoverStyles = !disableHover
      ? {
          transition: "all 0.3s ease",
          "&:hover": {
            ...(showBoxShadow && {
              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            }),
            transform: "translateY(-2px)",
          },
        }
      : {};

    return {
      ...baseStyles,
      ...borderStyles,
      ...shadowStyles,
      ...hoverStyles,
      ...containerProps?.sx,
    };
  };

  return (
    <Box sx={getContainerStyles()} {...containerProps}>
      {/* Badge removed - use images with embedded badges instead */}

      <Grid container spacing={getSpacingValue()} alignItems="center">
        {/* Image Section */}
        {hasImage && (
          <Grid
            item
            xs={12}
            md={6}
            order={{
              xs: 1,
              md: isImageLeft ? 1 : 2,
            }}
          >
            <ImageBlock image={blocks.image!} theme={theme} />
          </Grid>
        )}

        {/* Content Section */}
        <Grid
          item
          xs={12}
          md={hasImage ? 6 : 12}
          order={{
            xs: 2,
            md: isImageLeft ? 2 : 1,
          }}
        >
          <Stack spacing={3} alignItems="flex-start">
            {/* Header */}
            {blocks.header && <HeaderBlock header={blocks.header} theme={theme} />}

            {/* Content */}
            {blocks.content && <ContentBlock content={blocks.content} theme={theme} />}

            {/* Stats */}
            {blocks.stats && <StatsBlock stats={blocks.stats} theme={theme} />}

            {/* Meta */}
            {blocks.meta && <MetaBlock meta={blocks.meta} theme={theme} />}

            {/* Actions */}
            {blocks.actions && <ActionsBlock actions={blocks.actions} theme={theme} />}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HorizontalLayout;
